import { useMemo, useState } from 'react';
import ChartTypeSelector from '../ChartTypeSelector';
import DatasetSelector from '../DatasetSelector';
import SeriesConfigurator from '../SeriesConfigurator';
import AxisConfigurator from '../AxisConfigurator';
import ChartSpecificOptions from '../ChartSpecificOptions';
import CountryFeatures from '../CountryFeatures';
import DynamicPropertyConfig from '../DynamicPropertyConfig';
import JSONExporter from '../JSONExporter';
import ImportFromWagtail from '../ImportFromWagtail';
import SaveToWagtail from '../SaveToWagtail';
import { validateConfig } from '../../utils/validation';

import s from './ConfigPanel.module.scss';

const CollapsibleSection = ({ title, children, defaultExpanded = true }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <div className={s.Section}>
            <button
                type="button"
                className={s.SectionHeader}
                onClick={() => setIsExpanded(!isExpanded)}>
                <h2 className={s.SectionTitle}>{title}</h2>
                <svg
                    className={`${s.SectionChevron} ${isExpanded ? s.Expanded : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {isExpanded && <div className={s.SectionContent}>{children}</div>}
        </div>
    );
};

const GRID_SPAN_OPTIONS = ['1', '2', '3', '4', '5', '6'];

const ConfigPanel = ({
    config,
    updateConfig,
    onReset,
    onImport,
    datasets,
    datasetFields,
    filterableFields,
    currentDataset,
    loadingDatasets,
    loadingFields,
    onDatasetChange,
    datasetsError,
    fieldsError,
    addSeries,
    removeSeries,
    duplicateSeries,
    updateSeries,
    reorderSeries,
    fetchColumnValues,
    wagtailSource,
    onClearWagtailSource,
}) => {
    const selectedDataset = config.series?.[0]?.dataSource?.value || '';

    const validationErrors = useMemo(() => validateConfig(config), [config]);

    const handleYAxisChange = (yAxis) => updateConfig({ yAxis });
    const handleXAxisChange = (xAxis) => updateConfig({ xAxis });
    const handleChartTypeChange = (chartType) => updateConfig({ chartType });
    const handleTitleChange = (e) => updateConfig({ title: e.target.value });
    const handleGridSpanChange = (e) =>
        updateConfig({ gridSpan: e.target.value });
    const handleDynamicPropertyChange = (dynamicProperty) =>
        updateConfig({ dynamicProperty });
    const handleHideCountriesChange = (countries) =>
        updateConfig({ hideChartForCountries: countries });
    const handleIncludeInOverviewChange = (value) =>
        updateConfig({ includeInOverview: value });

    const standardChartTypes = [
        'line',
        'area',
        'column',
        'bar',
        'polar',
        'mixed',
    ];
    const supportsAggregation = standardChartTypes.includes(config.chartType);

    return (
        <div className={s.Root}>
            <div className={s.Header}>
                <h1 className={s.Title}>Graph Builder</h1>
                <button
                    onClick={onReset}
                    className={s.ResetButton}
                    title="Reset all configuration">
                    Reset
                </button>
            </div>

            <div className={s.Section}>
                <h2 className={s.SectionTitle}>Chart Type</h2>
                <ChartTypeSelector
                    value={config.chartType}
                    onChange={handleChartTypeChange}
                />
            </div>

            <div className={s.Section}>
                <h2 className={s.SectionTitle}>Data Source</h2>
                <DatasetSelector
                    datasets={datasets}
                    selectedDataset={selectedDataset}
                    loadingDatasets={loadingDatasets}
                    onDatasetChange={onDatasetChange}
                    datasetsError={datasetsError}
                />
            </div>

            {selectedDataset && (
                <CollapsibleSection title="Series">
                    <SeriesConfigurator
                        series={config.series}
                        onAddSeries={addSeries}
                        onRemoveSeries={removeSeries}
                        onDuplicateSeries={duplicateSeries}
                        onUpdateSeries={updateSeries}
                        onReorderSeries={reorderSeries}
                        datasetFields={datasetFields}
                        filterableFields={filterableFields}
                        currentDataset={currentDataset}
                        loadingFields={loadingFields}
                        validationErrors={validationErrors}
                        fetchColumnValues={fetchColumnValues}
                        chartType={config.chartType}
                    />
                </CollapsibleSection>
            )}

            {supportsAggregation && (
                <CollapsibleSection title="Data" defaultExpanded={false}>
                    <div className={s.CheckboxGroup}>
                        <label className={s.CheckboxLabel}>
                            <input
                                type="checkbox"
                                checked={
                                    config.aggregateDataByCategory || false
                                }
                                onChange={(e) =>
                                    updateConfig({
                                        aggregateDataByCategory:
                                            e.target.checked,
                                    })
                                }
                            />
                            <span>
                                Aggregate data by category (sum rows sharing the
                                same x-axis value)
                            </span>
                        </label>
                    </div>
                </CollapsibleSection>
            )}

            <CollapsibleSection title="Axes">
                <AxisConfigurator
                    yAxis={config.yAxis}
                    xAxis={config.xAxis}
                    chartType={config.chartType}
                    decimalPlaces={config.decimalPlaces}
                    onYAxisChange={handleYAxisChange}
                    onXAxisChange={handleXAxisChange}
                    onDecimalPlacesChange={(v) =>
                        updateConfig({ decimalPlaces: v })
                    }
                    validationErrors={validationErrors}
                />
            </CollapsibleSection>

            {config.chartType && (
                <div className={s.Section}>
                    <ChartSpecificOptions
                        chartType={config.chartType}
                        config={config}
                        updateConfig={updateConfig}
                        datasetFields={datasetFields}
                        loadingFields={loadingFields}
                        validationErrors={validationErrors}
                    />
                </div>
            )}

            <CollapsibleSection title="Title">
                <input
                    type="text"
                    className={s.Input}
                    value={config.title}
                    onChange={handleTitleChange}
                    placeholder="Enter chart title"
                />

                <div className={s.CheckboxGroup}>
                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.displayLargeTitle || false}
                            onChange={(e) =>
                                updateConfig({
                                    displayLargeTitle: e.target.checked,
                                })
                            }
                        />
                        <span>Display large title</span>
                    </label>

                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.showTitleInToc || false}
                            onChange={(e) =>
                                updateConfig({
                                    showTitleInToc: e.target.checked,
                                })
                            }
                        />
                        <span>Show in table of contents</span>
                    </label>
                </div>

                <div className={s.FieldRow}>
                    <label className={s.FieldLabel}>Grid Span</label>
                    <select
                        className={s.Select}
                        value={config.gridSpan || '1'}
                        onChange={handleGridSpanChange}>
                        {GRID_SPAN_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                </div>
            </CollapsibleSection>

            {selectedDataset && (
                <CollapsibleSection
                    title="Dynamic Property"
                    defaultExpanded={false}>
                    <DynamicPropertyConfig
                        dynamicProperty={config.dynamicProperty}
                        datasetFields={datasetFields}
                        loadingFields={loadingFields}
                        onChange={handleDynamicPropertyChange}
                    />
                </CollapsibleSection>
            )}

            <CollapsibleSection
                title="Country Features"
                defaultExpanded={false}>
                <CountryFeatures
                    hideChartForCountries={config.hideChartForCountries || []}
                    includeInOverview={config.includeInOverview || false}
                    onHideCountriesChange={handleHideCountriesChange}
                    onIncludeInOverviewChange={handleIncludeInOverviewChange}
                />
            </CollapsibleSection>

            <div className={s.Section}>
                <h2 className={s.SectionTitle}>Import / Export</h2>
                {wagtailSource && (
                    <SaveToWagtail
                        config={config}
                        wagtailSource={wagtailSource}
                        onClearSource={onClearWagtailSource}
                    />
                )}
                <ImportFromWagtail
                    onImport={onImport}
                    hasExistingConfig={!!config.chartType}
                />
                <JSONExporter config={config} onImport={onImport} />
            </div>
        </div>
    );
};

export default ConfigPanel;
